import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './GetPage.module.css';

// === /get — одна ссылка «поставить игру», которую не стыдно кинуть кому угодно ===
//
// Зачем (Адек, 28.08.2026): «скинул что-то и они скачали». На iPhone так нельзя,
// пока мы не в App Store — Apple не разрешает ставить мимо магазина, и TestFlight
// всегда требует сперва поставить сам TestFlight. Люди упираются не в TestFlight,
// а в ПОРЯДОК: жмут ссылку → их кидает в App Store ставить TestFlight → они
// открывают TestFlight сам по себе → пустой экран «Готово к тестированию» → ступор.
//
// Эта страница убирает ступор: один адрес blotme.app/get, дальше она сама смотрит,
// с чего зашли, и показывает ДВЕ кнопки по шагам. Промахнуться негде.
//
// Отдельно ловим встроенные браузеры мессенджеров: из них переход в TestFlight часто
// не срабатывает вообще. Поэтому кнопка «Скопировать ссылку» есть ВСЕГДА, а не только
// когда мы угадали мессенджер по подписи браузера — угадывание тут ненадёжное.

const TESTFLIGHT_LINK = 'https://testflight.apple.com/join/6b3bswe1';
const TESTFLIGHT_CODE = '6b3bswe1';                       // хвост ссылки = код для ручного ввода
const TESTFLIGHT_APP = 'https://apps.apple.com/app/testflight/id899247664';
const ANDROID_APK = '/api/download/android';              // отдаёт сервер, см. routes/download.js

// Что за устройство. iPad с iPadOS притворяется маком — ловим по касаниям.
const detect = () => {
  const ua = navigator.userAgent || '';
  const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/i.test(ua) || iPadOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

// Встроенный браузер мессенджера — там переход в TestFlight часто умирает молча.
const isInAppBrowser = () => {
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|Instagram|Telegram|WhatsApp|Viber|Line\/|VKAndroidApp|OKApp|MiuiBrowser/i.test(ua);
};

const GetPage = () => {
  // Код стола, если человек пришёл по приглашению (/join/КОД → сюда). Держим его на
  // виду и после установки: 08.09.2026 Адек — «чтоб человек не искал, куда вводить код».
  // Берём из адреса, а если его там нет — из памяти браузера, куда положила /join.
  const inviteCode = (() => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('code');
      if (fromUrl) return fromUrl.toUpperCase();
      return (window.localStorage.getItem('blotme_join_code') || '').toUpperCase() || null;
    } catch { return null; }
  })();

  const [platform, setPlatform] = useState('desktop');
  const [inApp, setInApp] = useState(false);
  const [copied, setCopied] = useState('');
  const [apk, setApk] = useState(null);        // размер и дата файла — спрашиваем у сервера

  useEffect(() => {
    setPlatform(detect());
    setInApp(isInAppBrowser());
  }, []);

  // Размер не зашиваем в текст: сборка меняется, а страница должна говорить правду.
  useEffect(() => {
    fetch('/api/download/android/info')
      .then((r) => r.json())
      .then(setApk)
      .catch(() => setApk(null));
  }, []);

  // Копирование с запасным путём: в старых webview clipboard недоступен.
  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); } catch (__) { /* не вышло — пусть копируют руками */ }
      document.body.removeChild(el);
    }
    setCopied(what);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className={styles.page}>
      <div className={styles.inner}>

        <header className={styles.head}>
          <span className={styles.wordmark}>
            <span className={styles.wmBlot}>blot</span>
            <span className={styles.wmDiamond}>◆</span>
            <span className={styles.wmMe}>Me</span>
          </span>
          <h1 className={styles.title}>{inviteCode ? 'Тебя зовут за стол' : 'Забрать игру'}</h1>
          <p className={styles.sub}>Базар Блот на четверых с живой видео-связью за столом.</p>
        </header>

        {inviteCode && (
          <div className={styles.warn} style={{ borderColor: '#E8B84B' }}>
            Поставь игру и войди — стол ждёт по коду{' '}
            <b style={{ color: '#E8B84B', letterSpacing: 1 }}>{inviteCode}</b>.
            <br />
            Код сохранён в этом браузере: если потеряешь — вернись на эту страницу.
          </div>
        )}

        {inApp && (
          <div className={styles.warn}>
            <b>Открой эту страницу в браузере.</b> Ты сейчас во встроенном окне мессенджера —
            из него установка часто не срабатывает. Нажми «…» вверху и выбери
            «Открыть в Safari» или «Открыть в браузере».
            <button className={styles.warnBtn} onClick={() => copy(window.location.href, 'page')}>
              {copied === 'page' ? 'Ссылка скопирована' : 'Скопировать ссылку'}
            </button>
          </div>
        )}

        {/* ───────────── iPhone / iPad ───────────── */}
        {platform === 'ios' && (
          <section className={styles.block}>
            <div className={styles.badge}>iPhone и iPad</div>

            <div className={styles.step}>
              <div className={styles.stepNum}>1</div>
              <div className={styles.stepBody}>
                <h2>Поставь TestFlight</h2>
                <p>Это приложение самой Apple — через него мы раздаём игру до выхода в App&nbsp;Store.</p>
                <a className={styles.btn} href={TESTFLIGHT_APP} target="_blank" rel="noreferrer">
                  Открыть App&nbsp;Store
                </a>
                <p className={styles.hint}>Уже стоит? Переходи ко второму шагу.</p>
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepNum}>2</div>
              <div className={styles.stepBody}>
                <h2>Забери BlotMe</h2>
                <p>Вернись сюда и нажми — TestFlight откроется уже с игрой.</p>
                <a className={`${styles.btn} ${styles.btnGold}`} href={TESTFLIGHT_LINK} target="_blank" rel="noreferrer">
                  Установить BlotMe
                </a>
              </div>
            </div>

            <details className={styles.rescue}>
              <summary>TestFlight открылся пустым — «Готово к тестированию»</summary>
              <p>
                Так бывает, если TestFlight ставился уже после нажатия ссылки — приглашение
                при этом теряется. Лечится за секунду: нажми <b>«Использовать код»</b> и введи
              </p>
              <div className={styles.codeRow}>
                <code className={styles.code}>{TESTFLIGHT_CODE}</code>
                <button className={styles.copyBtn} onClick={() => copy(TESTFLIGHT_CODE, 'code')}>
                  {copied === 'code' ? 'Скопировано' : 'Скопировать'}
                </button>
              </div>
              <p>Либо просто нажми кнопку второго шага ещё раз — теперь она сработает.</p>
            </details>
          </section>
        )}

        {/* ───────────── Android ───────────── */}
        {platform === 'android' && (
          <section className={styles.block}>
            <div className={styles.badge}>Android</div>

            <div className={styles.step}>
              <div className={styles.stepNum}>1</div>
              <div className={styles.stepBody}>
                <h2>Скачай и открой файл</h2>
                <p>Установка займёт полминуты. Google&nbsp;Play пока не нужен.</p>
                <a className={`${styles.btn} ${styles.btnGold}`} href={ANDROID_APK}>
                  Скачать BlotMe
                </a>
                <p className={styles.hint}>
                  {apk && apk.available
                    ? `Файл ${apk.sizeMb} МБ — лучше по Wi-Fi.`
                    : 'Файл крупный — лучше качать по Wi-Fi.'}
                </p>
              </div>
            </div>

            <details className={styles.rescue}>
              <summary>Телефон ругается «установка из неизвестного источника»</summary>
              <p>
                Это обычное предупреждение Android для приложений мимо Google&nbsp;Play.
                Нажми <b>«Настройки»</b> в самом окне, разреши установку для браузера
                и вернись назад — установка продолжится. Разрешение можно потом убрать.
              </p>
            </details>
          </section>
        )}

        {/* ───────────── Компьютер ───────────── */}
        {platform === 'desktop' && (
          <section className={styles.block}>
            <div className={styles.badge}>Компьютер</div>

            <div className={styles.step}>
              <div className={styles.stepNum}>1</div>
              <div className={styles.stepBody}>
                <h2>Играй прямо в браузере</h2>
                <p>Ставить не надо ничего — стол откроется здесь же.</p>
                <Link className={`${styles.btn} ${styles.btnGold}`} to="/app">Играть в браузере</Link>
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepNum}>2</div>
              <div className={styles.stepBody}>
                <h2>Хочешь на телефон</h2>
                <p>Открой на телефоне адрес <b>blotme.app/get</b> — он покажет, что делать именно там.</p>
                <button className={styles.btn} onClick={() => copy('https://blotme.app/get', 'link')}>
                  {copied === 'link' ? 'Ссылка скопирована' : 'Скопировать ссылку'}
                </button>
              </div>
            </div>
          </section>
        )}

        <p className={styles.guest}>
          Аккаунт заводить не нужно: на входе нажми <b>«Играть гостем»</b> и садись за стол.
        </p>

        {/* Определение по браузеру иногда врёт — даём переключиться руками. */}
        <div className={styles.switch}>
          <span>Показать для другого устройства:</span>
          {platform !== 'ios' && <button onClick={() => setPlatform('ios')}>iPhone</button>}
          {platform !== 'android' && <button onClick={() => setPlatform('android')}>Android</button>}
          {platform !== 'desktop' && <button onClick={() => setPlatform('desktop')}>Компьютер</button>}
        </div>

        <footer className={styles.foot}>
          <Link to="/">На главную</Link>
          <Link to="/privacy">Конфиденциальность</Link>
          <Link to="/terms">Условия</Link>
        </footer>

      </div>
    </div>
  );
};

export default GetPage;
